"use client";

import React, { useState, useEffect } from "react";
import {
  Users,
  UserPlus,
  Shield,
  Crown,
  UserCheck,
  CheckCircle2,
  XCircle,
  MoreVertical,
  Key,
  Trash2,
  Lock,
  RefreshCw,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/Button";

interface TeamMember {
  id: number;
  username: string;
  full_name: string;
  email: string | null;
  role: "admin" | "manager" | "agent" | string;
  is_active: number;
  created_at: string;
}

interface TeamManagementTabProps {
  currentUser: { id: number; username: string; role: string; fullName: string };
}

export default function TeamManagementTab({ currentUser }: TeamManagementTabProps) {
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [entitlements, setEntitlements] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Form State
  const [formData, setFormData] = useState({
    full_name: "",
    username: "",
    email: "",
    password: "",
    role: "agent" as "admin" | "manager" | "agent",
  });
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const fetchTeam = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch("/api/crm/team");
      const data = await res.json();
      if (res.ok && data.success) {
        setTeam(data.team || []);
      } else {
        setError(data.error || "Failed to load team");
      }

      // Fetch user plan seat entitlements
      try {
        const entRes = await fetch("/api/crm/entitlements");
        if (entRes.ok) {
          const entData = await entRes.json();
          if (entData.success) setEntitlements(entData.entitlements);
        }
      } catch (e) {}
    } catch (err: any) {
      setError(err.message || "Failed to connect to team service");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeam();
  }, []);

  const handleCreateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormSubmitting(true);
    setFormError(null);

    try {
      const res = await fetch("/api/crm/team", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      const data = await res.json();

      if (res.ok && data.success) {
        setShowAddModal(false);
        setFormData({
          full_name: "",
          username: "",
          email: "",
          password: "",
          role: "agent",
        });
        fetchTeam();
      } else {
        setFormError(data.error || "Failed to create team member");
      }
    } catch (err: any) {
      setFormError(err.message || "Failed to connect to team service");
    } finally {
      setFormSubmitting(false);
    }
  };

  const handleToggleStatus = async (user: TeamMember) => {
    try {
      const newStatus = user.is_active === 1 ? 0 : 1;
      const res = await fetch(`/api/crm/team/${user.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ is_active: newStatus }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchTeam();
      } else {
        alert(data.error || "Failed to update member status");
      }
    } catch (err: any) {
      alert(err.message || "Failed to update member status");
    }
  };

  const handleDeleteMember = async (user: TeamMember) => {
    if (!confirm(`Are you sure you want to permanently delete ${user.full_name} (${user.username})?`)) {
      return;
    }

    try {
      const res = await fetch(`/api/crm/team/${user.id}`, {
        method: "DELETE",
      });
      const data = await res.json();
      if (res.ok && data.success) {
        fetchTeam();
      } else {
        alert(data.error || "Failed to delete team member");
      }
    } catch (err: any) {
      alert(err.message || "Failed to delete team member");
    }
  };

  const admins = team.filter((m) => m.role === "admin");
  const managers = team.filter((m) => m.role === "manager");
  const agents = team.filter((m) => m.role === "agent" || m.role === "sales");

  return (
    <div className="space-y-6">
      {/* Top Stat Overview Bar */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="glass-card-premium rounded-2xl p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-blue-500/10 text-blue-500">
            <Users className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs text-text-muted font-medium">Total Team Members</span>
            <div className="text-2xl font-heading font-black text-foreground">{team.length}</div>
          </div>
        </div>

        <div className="glass-card-premium rounded-2xl p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-amber-500/10 text-amber-500">
            <Crown className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs text-text-muted font-medium">Super Admins</span>
            <div className="text-2xl font-heading font-black text-foreground">{admins.length}</div>
          </div>
        </div>

        <div className="glass-card-premium rounded-2xl p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-indigo-500/10 text-indigo-500">
            <Shield className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs text-text-muted font-medium">Sales Managers</span>
            <div className="text-2xl font-heading font-black text-foreground">{managers.length}</div>
          </div>
        </div>

        <div className="glass-card-premium rounded-2xl p-4 flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-500/10 text-emerald-500">
            <UserCheck className="w-5 h-5" />
          </div>
          <div>
            <span className="text-xs text-text-muted font-medium">Sales Reps &amp; Telecallers</span>
            <div className="text-2xl font-heading font-black text-foreground">{agents.length}</div>
          </div>
        </div>
      </div>

      {/* SaaS Seat Entitlements Indicator Banner */}
      {entitlements && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 p-3.5 px-5 rounded-2xl bg-primary/5 border border-primary/20 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-bold text-primary">{entitlements.planName} Plan:</span>
            <span className="font-semibold text-slate-700 dark:text-slate-300">
              {entitlements.maxSeats === 0
                ? "Unlimited Seats Included"
                : `${team.length} of ${entitlements.maxSeats} Seats Used (${entitlements.seatsRemaining} remaining)`}
            </span>
            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full font-bold">
              Zero Per-Seat SaaS Tax
            </span>
          </div>
          {!entitlements.canAddSeat && (
            <span className="text-amber-600 dark:text-amber-400 font-bold flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> Plan Seat Limit Reached
            </span>
          )}
        </div>
      )}

      {/* Action Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 p-4 rounded-2xl bg-slate-100/70 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800">
        <div>
          <h3 className="text-base font-heading font-bold text-foreground">
            Team Roster &amp; Access Control (RBAC)
          </h3>
          <p className="text-xs text-text-muted mt-0.5">
            Manage your company sales team, department managers, and system administrators.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={fetchTeam}
            className="p-2 rounded-xl border border-slate-300 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-800 text-foreground text-xs flex items-center gap-1.5 transition-colors"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>Refresh</span>
          </button>

          {currentUser.role === "admin" && (
            <Button
              onClick={() => setShowAddModal(true)}
              variant="gradient"
              size="sm"
              className="flex items-center gap-1.5 shadow-md shadow-primary/20"
            >
              <UserPlus className="w-4 h-4" />
              <span>Add Team Member</span>
            </Button>
          )}
        </div>
      </div>

      {/* Team Table */}
      <div className="glass-card-premium rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-slate-200/80 dark:border-slate-800/80 bg-slate-50/50 dark:bg-slate-900/40 text-[11px] font-mono uppercase text-text-muted">
                <th className="py-3.5 px-4 font-semibold">User</th>
                <th className="py-3.5 px-4 font-semibold">Role</th>
                <th className="py-3.5 px-4 font-semibold">Email</th>
                <th className="py-3.5 px-4 font-semibold">Status</th>
                <th className="py-3.5 px-4 font-semibold">Joined Date</th>
                {currentUser.role === "admin" && (
                  <th className="py-3.5 px-4 font-semibold text-right">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200/60 dark:divide-slate-800/60">
              {team.map((member) => (
                <tr key={member.id} className="hover:bg-slate-50/50 dark:hover:bg-slate-900/30 transition-colors">
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-800 flex items-center justify-center font-bold text-foreground text-xs shrink-0">
                        {member.full_name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-heading font-bold text-foreground flex items-center gap-1.5">
                          <span>{member.full_name}</span>
                          {member.username === currentUser.username && (
                            <span className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-cyan-500/10 text-cyan-500 font-normal">
                              You
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-text-muted font-mono">@{member.username}</div>
                      </div>
                    </div>
                  </td>

                  {/* Role Badge */}
                  <td className="py-3 px-4">
                    {member.role === "admin" && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-heading font-bold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20">
                        <Crown className="w-3 h-3" />
                        <span>Super Admin</span>
                      </span>
                    )}
                    {member.role === "manager" && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-heading font-bold bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 border border-indigo-500/20">
                        <Shield className="w-3 h-3" />
                        <span>Sales Manager</span>
                      </span>
                    )}
                    {(member.role === "agent" || member.role === "sales") && (
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-heading font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                        <UserCheck className="w-3 h-3" />
                        <span>Sales Agent</span>
                      </span>
                    )}
                  </td>

                  <td className="py-3 px-4 text-text-muted font-mono text-[11px]">
                    {member.email || "—"}
                  </td>

                  <td className="py-3 px-4">
                    {member.is_active === 1 ? (
                      <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 dark:text-emerald-400 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        <span>Active</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] text-rose-500 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />
                        <span>Deactivated</span>
                      </span>
                    )}
                  </td>

                  <td className="py-3 px-4 text-text-muted text-[11px]">
                    {new Date(member.created_at).toLocaleDateString()}
                  </td>

                  {currentUser.role === "admin" && (
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {member.id !== currentUser.id && (
                          <>
                            <button
                              onClick={() => handleToggleStatus(member)}
                              className="px-2.5 py-1 rounded-lg border border-slate-300 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 text-[11px] text-text-muted hover:text-foreground transition-colors"
                            >
                              {member.is_active === 1 ? "Deactivate" : "Activate"}
                            </button>

                            <button
                              onClick={() => handleDeleteMember(member)}
                              className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-colors"
                              title="Delete Member"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Role Permission Matrix Card */}
      <div className="p-5 rounded-2xl bg-slate-100/70 dark:bg-slate-900/50 border border-slate-200 dark:border-slate-800">
        <h4 className="font-heading font-bold text-xs text-foreground uppercase tracking-wider mb-3 flex items-center gap-2">
          <Lock className="w-3.5 h-3.5 text-cyan-400" />
          <span>Role Permissions Matrix</span>
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="font-bold text-amber-500 flex items-center gap-1.5 mb-2">
              <Crown className="w-3.5 h-3.5" />
              <span>Super Administrator</span>
            </div>
            <ul className="space-y-1 text-text-muted text-[11px]">
              <li>✓ Full system &amp; pipeline access</li>
              <li>✓ Manage team accounts &amp; roles</li>
              <li>✓ Export full CSV database</li>
              <li>✓ Configure WhatsApp &amp; API keys</li>
            </ul>
          </div>

          <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="font-bold text-indigo-400 flex items-center gap-1.5 mb-2">
              <Shield className="w-3.5 h-3.5" />
              <span>Sales Manager</span>
            </div>
            <ul className="space-y-1 text-text-muted text-[11px]">
              <li>✓ View all team pipeline deals</li>
              <li>✓ Approve stage advances &amp; values</li>
              <li>✓ Reassign leads across reps</li>
              <li>✗ Cannot export CSV or delete team</li>
            </ul>
          </div>

          <div className="p-3 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
            <div className="font-bold text-emerald-400 flex items-center gap-1.5 mb-2">
              <UserCheck className="w-3.5 h-3.5" />
              <span>Sales Agent / Front Desk</span>
            </div>
            <ul className="space-y-1 text-text-muted text-[11px]">
              <li>✓ Manage assigned leads &amp; calls</li>
              <li>✓ Add notes &amp; schedule callbacks</li>
              <li>✓ Send template WhatsApp messages</li>
              <li>✗ Cannot delete leads or view unassigned</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-sm animate-in fade-in">
          <div className="w-full max-w-md glass-card-premium rounded-2xl p-6 shadow-2xl border border-slate-200 dark:border-slate-800">
            <div className="flex items-center justify-between pb-4 border-b border-slate-200 dark:border-slate-800 mb-4">
              <div className="flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-primary" />
                <h3 className="font-heading font-bold text-sm text-foreground">Add New Team Member</h3>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="p-1 rounded-lg text-text-muted hover:text-foreground"
              >
                ✕
              </button>
            </div>

            {formError && (
              <div className="p-3 rounded-xl bg-rose-500/10 border border-rose-500/20 text-rose-500 text-xs mb-4 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <form onSubmit={handleCreateMember} className="space-y-3.5">
              <div>
                <label className="block text-[11px] font-mono font-medium text-text-muted mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-foreground text-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-medium text-text-muted mb-1">
                  Username (Login ID)
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. rahul"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-foreground text-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-medium text-text-muted mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="rahul@company.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-foreground text-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-medium text-text-muted mb-1">
                  Initial Password
                </label>
                <input
                  type="password"
                  required
                  placeholder="Minimum 6 characters"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-foreground text-xs focus:outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-mono font-medium text-text-muted mb-1">
                  Role &amp; Permissions
                </label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as any })}
                  className="w-full px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-foreground text-xs focus:outline-none focus:border-primary"
                >
                  <option value="agent">Sales Agent (View/edit assigned leads)</option>
                  <option value="manager">Sales Manager (Pipeline oversight &amp; approvals)</option>
                  <option value="admin">Super Administrator (Full business access)</option>
                </select>
              </div>

              <div className="pt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-700 text-xs font-semibold text-text-muted hover:text-foreground transition-colors"
                >
                  Cancel
                </button>
                <Button
                  type="submit"
                  variant="gradient"
                  size="sm"
                  disabled={formSubmitting}
                >
                  {formSubmitting ? "Creating..." : "Save Member"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
